import type { SetupGuide } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { isEmptyString } from '@/utils/string';
import { SegmentAuthAction } from './actions';

/**
 * The setup-guide onboarding step the Segment source connector completes.
 *
 * Connecting marks the integration step done and disconnecting un-marks it. This
 * is invisible on the connector screen — the guide lives elsewhere — but onboarding
 * reporting reads it, so a connect that skipped it would leave the account looking
 * like it never integrated.
 *
 * Both writes are best-effort by design: the connector screen reports the outcome
 * of the Segment call, and a failed guide update must not turn a successful connect
 * into an error. Callers do not await them.
 *
 * The endpoint takes the whole record, so each write reads it first.
 *
 * Neither write touches the query cache. A caller that has the guide on screen —
 * the home and onboarding screens read it through `useSetupGuide` — must invalidate
 * `['setupGuide', workspaceId]` itself, because these resolve after the connect has
 * already been reported.
 */

const SEGMENT_INTEGRATION_METHOD = 'segment';

async function readSetupGuide(workspaceId: number): Promise<SetupGuide> {
  const { setup_guide: setupGuide } = await hydraUiApi.setupGuide.getCurrent({
    params: { path: { environment_id: String(workspaceId) } },
  });

  return setupGuide;
}

async function writeSetupGuide(workspaceId: number, setupGuide: SetupGuide): Promise<void> {
  await hydraUiApi.setupGuide.updateCurrent({
    params: { path: { environment_id: String(workspaceId) } },
    body: { setup_guide: setupGuide },
  });
}

/**
 * Whether connecting should claim the integration step.
 *
 * It claims a guide with no method recorded, and re-stamps one already attributed
 * to Segment that never got a timestamp. A guide claimed by another integration is
 * left alone, so connecting Segment does not overwrite how the account actually
 * onboarded.
 */
export function shouldMarkSegmentConnected(
  setupGuide: Pick<SetupGuide, 'integration_method' | 'integration_connected_at'>,
): boolean {
  const method = setupGuide.integration_method;

  if (isEmptyString(method)) return true;
  if (method !== SEGMENT_INTEGRATION_METHOD) return false;

  // `0` means never stamped, not "stamped at the epoch" — the backend sends it and
  // the guide's own completion check reads it as unset.
  const connectedAt = setupGuide.integration_connected_at;
  return connectedAt == null || connectedAt === 0;
}

export type MarkSegmentConnectedInput = {
  workspaceId: number;
  /** Unix **seconds**, as the field is an int32. Defaults to now. */
  connectedAtUnixSeconds?: number;
};

/** Records the connection against the integration step. Resolves even when the write fails. */
export async function markSegmentConnected({
  workspaceId,
  connectedAtUnixSeconds = Math.floor(Date.now() / 1000),
}: MarkSegmentConnectedInput): Promise<void> {
  try {
    const setupGuide = await readSetupGuide(workspaceId);
    if (!shouldMarkSegmentConnected(setupGuide)) return;

    await writeSetupGuide(workspaceId, {
      ...setupGuide,
      integration_connected_at: connectedAtUnixSeconds,
      integration_method: SEGMENT_INTEGRATION_METHOD,
    });
  } catch {
    // Best-effort: see the note above.
  }
}

export type ReleaseSegmentSetupGuideStepInput = {
  workspaceId: number;
  /** The action that just succeeded. Only `Delete` releases the step. */
  action: SegmentAuthAction;
};

/**
 * Releases the integration step when the connection is deleted.
 *
 * Two conditions, both Ember's. Only `Delete` releases it — disabling leaves it,
 * because the integration is still how the account onboarded — and only when
 * Segment is the recorded method, since another integration's claim is not
 * Segment's to clear.
 *
 * The action is a parameter rather than the caller's responsibility because
 * disable and delete run through one path on the screen, which is where dropping
 * the distinction would be easy.
 */
export async function releaseSegmentSetupGuideStep({
  workspaceId,
  action,
}: ReleaseSegmentSetupGuideStepInput): Promise<void> {
  if (action !== SegmentAuthAction.Delete) return;

  try {
    const setupGuide = await readSetupGuide(workspaceId);
    if (setupGuide.integration_method !== SEGMENT_INTEGRATION_METHOD) return;

    await writeSetupGuide(workspaceId, {
      ...setupGuide,
      integration_connected_at: null,
      integration_method: null,
    });
  } catch {
    // Best-effort: see the note above.
  }
}
